import { f7, useStore } from 'framework7-vue'
import { DateTime } from 'luxon'

import Reservation from '@/assets/classes/parking/Reservation'
import { recordFailure } from '@/assets/composables/diagnostics'
import { getCardLoans, getReservations, reserveSlot } from '@/assets/composables/endpoints'
import { trackParkingReservationSuccess } from '@/assets/composables/events'
import { showPreloader } from '@/assets/composables/preloader'
import { PartOfDay, ReservationRich } from '@/assets/composables/types'
import { notify, processError } from '@/assets/composables/utils'
import store from '@/js/store'

export default class Reservations {
  static #cardLoanGeneration = 0
  static #cardLoanRefreshGeneration: number | null = null
  static #cardLoanAppends = new Map<string, Promise<void>>()
  constructor() {}

  static async #checkIfReservationExists(dateIso: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const reservationDate = DateTime.fromISO(dateIso, { zone: 'Europe/Bratislava' })
      const reservation: ReservationRich = Reservations.listUpcomingReservations().find(
        (res: Reservation) => res.date?.hasSame(reservationDate, 'day'),
      )
      if (reservation) {
        f7.dialog.confirm(
          'Upozorňujeme Vás, že na tento deň už máte vytvorenú rezerváciu. Odoslaním formulára <b>prepíšete existujúcu rezerváciu</b>.',
          () => resolve(true),
          () => resolve(false),
        )
      } else {
        resolve(true)
      }
    })
  }

  static async makeReservation(dateIso: string | null, time: PartOfDay | null) {
    if (!dateIso || !time) return

    if (await this.#checkIfReservationExists(dateIso)) {
      const closePreloader = showPreloader('Vytváram rezerváciu')
      try {
        await reserveSlot(dateIso, time)

        notify('Rezervácia bola úspešne vytvorená', () => {
          f7.popup.close('.parking-reservation-form')
        })

        trackParkingReservationSuccess({ date: dateIso, partOfDay: time })
        await this.fetchNewData()
      } catch (error) {
        processError(error)
      } finally {
        closePreloader()
      }
    }
  }

  static listUpcomingReservations() {
    return useStore('reservations').value
  }

  static listHistoricalReservations() {
    return useStore('historicalReservations').value
  }

  static async #getUpcomingReservations() {
    try {
      const reservations = await getReservations('upcoming')
      await store.dispatch('setReservations', reservations)
    } catch (e) {
      processError(e, 'Nepodarilo sa načítať budúce rezervácie.')
    }
  }

  static async #getHistoricalReservations() {
    try {
      const reservations = await getReservations('historical', false)
      await store.dispatch('setHistoricalReservations', reservations)
    } catch (e) {
      processError(e, 'Nepodarilo sa načítať historické rezervácie.')
    }
  }

  static async #getReservationsWithMissingCard() {
    try {
      const reservations = await getReservations('missingCard')
      await store.dispatch('setCardMissingReservations', reservations)
    } catch (e) {
      processError(e, 'Nepodarilo sa načítať rezervácie s nevrátenou kartou.')
    }
  }

  static async fetchNewData() {
    await Promise.all([
      this.#getUpcomingReservations(),
      this.#getHistoricalReservations(),
      this.#getReservationsWithMissingCard(),
      this.fetchCardLoans(),
    ])
  }

  static async fetchCardLoans(append = false) {
    const userId = store.state.user?.id
    if (!userId) return

    if (!append) {
      const generation = ++this.#cardLoanGeneration
      this.#cardLoanRefreshGeneration = generation
      this.#cardLoanAppends.clear()
      try {
        await this.#fetchCardLoanPage(userId, generation, 0, false)
      } finally {
        if (this.#cardLoanRefreshGeneration === generation) this.#cardLoanRefreshGeneration = null
      }
      return
    }

    const generation = this.#cardLoanGeneration
    // An append belongs to a completed first page, not to the list that a
    // pending refresh is about to replace.
    if (this.#cardLoanRefreshGeneration === generation) return
    const offset = store.state.cardLoans.length
    const key = `${generation}:${userId}:${offset}`
    const existing = this.#cardLoanAppends.get(key)
    if (existing) return existing
    const request = this.#fetchCardLoanPage(userId, generation, offset, true)
    this.#cardLoanAppends.set(key, request)
    try {
      await request
    } finally {
      if (this.#cardLoanAppends.get(key) === request) this.#cardLoanAppends.delete(key)
    }
  }

  static async #fetchCardLoanPage(
    userId: string,
    generation: number,
    offset: number,
    append: boolean,
  ) {
    const isCurrent = () =>
      store.state.user?.id === userId &&
      generation === this.#cardLoanGeneration &&
      (!append || offset === store.state.cardLoans.length)
    try {
      const result = await getCardLoans(offset)
      if (isCurrent()) {
        await store.dispatch('setCardLoans', { ...result, append })
      }
    } catch (error) {
      if (store.state.user?.id !== userId) return
      if (isCurrent()) {
        await processError(error, 'Nepodarilo sa načítať karty na vrátenie.')
      } else {
        // A superseded request must not interrupt the fresh list with a dialog.
        // Real technical failures still use the common sanitized capture path;
        // expected business/security outcomes are filtered by that reporter.
        await recordFailure(error, { area: 'api', operation: 'load' })
      }
    }
  }
}
